import React from "react";
import { Box, Stack, SxProps, Theme } from "@mui/material";
import { DateTimePickerProps } from "@mui/x-date-pickers";
import { TimeRange } from "src/app/components/TimeRangeFilter/timeTypes";
import dayjs from "dayjs";
import { TimeRangePickerErrorProp } from "src/app/components/Time/types";
import HebDateTimePicker from "src/app/components/Time/HebDateTimePicker";

const dateTimeViews: DateTimePickerProps<dayjs.Dayjs, dayjs.Dayjs>["views"] = ["day", "hours", "minutes"];
const dateViews: DateTimePickerProps<dayjs.Dayjs, dayjs.Dayjs>["views"] = ["day"];
const timeViews: DateTimePickerProps<dayjs.Dayjs, dayjs.Dayjs>["views"] = ["hours", "minutes"];

type Props = {
  timeRange: TimeRange;
  composition?: "full" | "timeOnly" | "dateOnly";
  direction?: "row" | "column";
  error?: TimeRangePickerErrorProp;
  futureOnly?: boolean;
  sx?: SxProps<Theme>;
  setTimeRange: (timeRange: TimeRange) => void;
};

const TimeRangePicker: React.FC<Props> = ({
  sx,
  timeRange,
  composition = "full",
  direction = "column",
  futureOnly,
  error,
  setTimeRange,
}) => {
  // A "row" layout would overflow a narrow phone, so stack the two pickers
  // vertically on mobile and only place them side-by-side from `sm` up.
  const stackDirection =
    direction === "row" ? ({ xs: "column", sm: "row" } as const) : direction;
  const startTime = dayjs(timeRange.startTime);
  const endTime = dayjs(timeRange.endTime);
  const now = dayjs();
  const fromMinDate = futureOnly ? now : undefined;
  const fromMinTime =
    futureOnly && startTime.startOf("day").isSame(now.startOf("day"))
      ? now
      : undefined;

  const handleDateTimeChange = (
    updateType: "date" | "time" | "datetime",
    field: "startTime" | "endTime",
    newDateTime: dayjs.Dayjs | null
  ) => {
    if (!newDateTime) return;
    const currDateTime = field === "startTime" ? startTime : endTime;

    if (updateType === "date") {
      // update only the date, not time
      newDateTime.hour(currDateTime?.hour() ?? newDateTime.hour());
      newDateTime.minute(currDateTime?.minute() ?? newDateTime.minute());
    } else if (updateType === "time") {
      // update only the time, not date
      newDateTime.year(currDateTime?.year() ?? newDateTime.year());
      newDateTime.month(currDateTime?.month() ?? newDateTime.month());
      newDateTime.date(currDateTime?.date() ?? newDateTime.date());
    }

    setTimeRange({
      ...timeRange,
      [field]: newDateTime.toDate(),
    });
  };

  const FromDate = (
    <HebDateTimePicker
      label="מתאריך"
      value={startTime}
      views={dateViews}
      error={error}
      onChange={(newDateTime) =>
        handleDateTimeChange("date", "startTime", newDateTime)
      }
    />
  );

  const ToDate = (
    <HebDateTimePicker
      label="לתאריך"
      value={endTime}
      views={dateViews}
      error={error}
      onChange={(newDateTime) =>
        handleDateTimeChange("date", "endTime", newDateTime)
      }
    />
  );

  const FromTime = (
    <HebDateTimePicker
      label="משעה"
      value={startTime}
      minDate={fromMinTime}
      views={timeViews}
      error={error}
      onChange={(newDateTime) => {
        handleDateTimeChange("time", "startTime", newDateTime);
      }}
    />
  );

  const ToTime = (
    <HebDateTimePicker
      label="לשעה"
      value={endTime}
      views={timeViews}
      error={error}
      onChange={(newDateTime) =>
        handleDateTimeChange("time", "endTime", newDateTime)
      }
    />
  );

  const fromDateTime = (
    <HebDateTimePicker
      label="מתאריך ושעה"
      value={startTime}
      minDate={fromMinDate}
      views={dateTimeViews}
      error={error}
      onChange={(newDateTime) => {
        handleDateTimeChange("datetime", "startTime", newDateTime);
      }}
    />
  );

  const toDateTime = (
    <HebDateTimePicker
      label="לתאריך ושעה"
      value={endTime}
      views={dateTimeViews}
      error={error}
      onChange={(newDateTime) =>
        handleDateTimeChange("datetime", "endTime", newDateTime)
      }
    />
  );

  const FullComposition = (
    <Box sx={{ ...sx }}>
      <Stack spacing={2} direction={stackDirection}>
        <Box sx={{ width: "100%" }}>{fromDateTime}</Box>
        <Box sx={{ width: "100%" }}>{toDateTime}</Box>
      </Stack>
    </Box>
  );

  const TimeOnlyComposition = (
    <Box sx={{ ...sx }}>
      <Stack spacing={2} direction={stackDirection}>
        <Box sx={{ width: "100%" }}>{FromTime}</Box>
        <Box sx={{ width: "100%" }}>{ToTime}</Box>
      </Stack>
    </Box>
  );

  const DateOnlyComposition = (
    <Box sx={{ ...sx }}>
      <Stack spacing={1} direction={stackDirection}>
        <Box sx={{ width: "100%" }}>{FromDate}</Box>
        <Box sx={{ width: "100%" }}>{ToDate}</Box>
      </Stack>
    </Box>
  );

  switch (composition) {
    case "full":
      return FullComposition;
    case "timeOnly":
      return TimeOnlyComposition;
    case "dateOnly":
      return DateOnlyComposition;
  }
};

export default TimeRangePicker;
